const MainSearch = class extends HTMLElement {
  constructor() {
    super();

    // Clicking close button
    this.querySelectorAll('.main-search__close').forEach((el) => {
      el.addEventListener('click', (evt) => {
        evt.preventDefault();
        evt.stopPropagation();
        
        const searchInput = this.querySelector('.main-search__input');
        if (searchInput) {
          searchInput.blur();
        }
        
        const scrollY = window.scrollY;
        
        document.body.classList.remove('show-search');
        
        requestAnimationFrame(() => {
          window.scrollTo(0, scrollY);
        });
      });
    });

    const searchInput = this.querySelector('.main-search__input');
    const clearButton = this.querySelector('.main-search__clear');
    
    if (searchInput && clearButton) {
      const toggleClearButton = () => {
        if (searchInput.value.length > 0) {
          clearButton.style.display = 'block';
        } else {
          clearButton.style.display = 'none';
        }
      };
      
      clearButton.addEventListener('click', (evt) => {
        evt.preventDefault();
        searchInput.value = '';
        searchInput.focus();
        toggleClearButton();
        
        searchInput.dispatchEvent(new Event('input', { bubbles: true }));
        searchInput.dispatchEvent(new Event('change', { bubbles: true }));
      });
      
      searchInput.addEventListener('input', toggleClearButton);
      searchInput.addEventListener('change', toggleClearButton);
      
      toggleClearButton();
    }

    // Pressing escape key
    this.querySelector('.main-search__input').addEventListener('keyup', (evt) => {
      if (evt.key === 'Escape') {
        this.querySelector('.main-search__close').dispatchEvent(new Event('click'));
      }
    });

    // Move search suggestions wrapper outside form for grid layout
    this.moveSearchSuggestions();

    // Search as you type
    if (this.dataset.quickSearch === 'true') {
      this.initQuickSearch();
    }

    this.loadEmptyStateOnOpen();
    
    this.addEventListener('suggestions-loaded', () => {
      this.moveSearchSuggestions();
      const searchInput = this.querySelector('.main-search__input');
      if (searchInput && searchInput.value) {
        setTimeout(() => {
          this.updateViewAllButtonsCount(searchInput.value);
        }, 100);
      }
    });
  }

  moveSearchSuggestions() {
    const form = this.querySelector('.main-search__form');
    const container = this.querySelector('.main-search__container');
    const resultsBox = this.querySelector('.main-search__results');
    
    if (!form || !container || !resultsBox) return;
    
    const moveElement = () => {
      const searchSuggestions = this.querySelector('search-suggestions');
      if (searchSuggestions) {
        const hasContent = searchSuggestions.querySelector('.search-suggestions-wrapper');
        if (hasContent && searchSuggestions.parentElement === form) {
          container.insertBefore(searchSuggestions, resultsBox);
        }
      }
    };
    
    moveElement();
    
    const observer = new MutationObserver(() => {
      moveElement();
    });
    
    observer.observe(form, { childList: true, subtree: true });
    observer.observe(container, { childList: true });
    
    if (this.querySelector('search-suggestions')) {
      this.querySelector('search-suggestions').addEventListener('suggestions-loaded', moveElement);
    }
    
    setTimeout(moveElement, 100);
    setTimeout(moveElement, 500);
    setTimeout(moveElement, 1000);
  }

  loadEmptyStateOnOpen() {
    // watch for when search overlay is shown!
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const isSearchOpen = document.body.classList.contains('show-search');
          const resultsBox = this.querySelector('.main-search__results');
          const searchInput = this.querySelector('.main-search__input');
          
          if (isSearchOpen && !searchInput.value && !resultsBox.hasChildNodes() && !this.hasLoadedEmptyState) {
            this.hasLoadedEmptyState = true;
            this.loadEmptyState();
          } else if (!isSearchOpen) {
            this.hasLoadedEmptyState = false;
          }
        }
      });
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });

    if (document.body.classList.contains('show-search')) {
      const searchInput = this.querySelector('.main-search__input');
      if (!searchInput.value) {
        this.loadEmptyState();
      }
    }
  }

  loadEmptyState() {
    const resultsBox = this.querySelector('.main-search__results');
    
    const emptyStateTemplate = document.getElementById('search-empty-state-template');
    if (emptyStateTemplate) {
      const emptyState = emptyStateTemplate.content.cloneNode(true);
      if (emptyState.querySelector('.predictive-search-results__empty-state')) {
        this.classList.add('main-search--empty-state');
        resultsBox.innerHTML = '';
        resultsBox.appendChild(emptyState);
        return;
      }
    }
    
    if (theme.Shopify.features.predictiveSearch) {
      const emptyStateUrl = new URL(theme.routes.base + theme.routes.predictiveSearch);
      emptyStateUrl.searchParams.set('q', '');
      emptyStateUrl.searchParams.set('section_id', 'predictive-search');
      
      fetch(emptyStateUrl, { method: 'get' })
        .then((response) => response.text())
        .then((text) => {
          const template = document.createElement('template');
          template.innerHTML = text;
          const emptyState = template.content.querySelector('.predictive-search-results__content--empty');
          if (emptyState) {
            this.classList.add('main-search--empty-state');
            resultsBox.innerHTML = '';
            resultsBox.appendChild(emptyState.cloneNode(true));
          }
        })
        .catch(() => {
        });
    }
  }

  initQuickSearch() {
    const searchInput = this.querySelector('.main-search__input');
    const searchTimeoutThrottle = 500;
    const resultLimit = 10;
    const includeMeta = this.dataset.quickSearchMeta === 'true';
    const productsLimit = parseInt(this.dataset.predictiveProductsLimit || '10', 10);
    const articlesLimit = parseInt(this.dataset.predictiveArticlesLimit || '2', 10);
    const pagesLimit = parseInt(this.dataset.predictivePagesLimit || '2', 10);
    let searchTimeoutID = -1;
    let searchAbortController = null;

    const handleInputChange = () => {
      const resultsBox = this.querySelector('.main-search__results');
      const valueToSearch = searchInput.value;

      // Only search if search string longer than 2, and it has changed
      if (valueToSearch.length && valueToSearch !== this.oldSearchValue) {
        // Remove empty state class when user starts typing
        this.classList.remove('main-search--empty-state');
        // Save previous value
        this.oldSearchValue = valueToSearch;

        // Kill outstanding ajax request
        if (searchAbortController !== null) {
          searchAbortController.abort('Existing request not needed');
          searchAbortController = null;
        }

        // Kill previous search
        clearTimeout(searchTimeoutID);

        // Create URL for full search results
        const form = searchInput.closest('form');
        const linkURL = new URL(form.action);
        const formParams = new FormData(form);
        const paramMap = new Map();
        for (const [key, value] of formParams.entries()) {
          if (paramMap.has(key)) {
            const existing = paramMap.get(key);
            if (Array.isArray(existing)) {
              existing.push(value);
            } else {
              paramMap.set(key, [existing, value]);
            }
          } else {
            paramMap.set(key, value);
          }
        }
        paramMap.forEach((value, key) => {
          if (Array.isArray(value)) {
            value.forEach(v => linkURL.searchParams.append(key, v));
          } else {
            linkURL.searchParams.set(key, value);
          }
        });

        // Show loading
        this.classList.remove('main-search--has-results', 'main-search--results-on-multiple-lines', 'main-search--no-results');
        this.classList.add('main-search--loading');
        if (!this.querySelector('.main-search__results-spinner')) {
          resultsBox.innerHTML = '<div class="main-search__results-spinner"><div class="loading-spinner"></div></div>';
        }

        // Do next search (in X milliseconds)
        searchTimeoutID = setTimeout(() => {
          searchAbortController = new AbortController();
          let ajaxUrl;

          if (theme.Shopify.features.predictiveSearch) {
            // use the API
            ajaxUrl = new URL(theme.routes.base + theme.routes.predictiveSearch);
            ajaxUrl.searchParams.set('q', valueToSearch);
            ajaxUrl.searchParams.set('section_id', 'predictive-search');
            ajaxUrl.searchParams.set('resources[limit_scope]', 'each');
            ajaxUrl.searchParams.set('resources[limit]', Math.max(productsLimit, articlesLimit, pagesLimit));
            ajaxUrl.searchParams.set('resources[products][limit]', productsLimit);
            ajaxUrl.searchParams.set('resources[articles][limit]', articlesLimit);
            ajaxUrl.searchParams.set('resources[pages][limit]', pagesLimit);
            ajaxUrl.searchParams.set('resources[options][fields]', includeMeta ? 'title,product_type,variants.title,vendor,tag,variants.sku' : 'title,product_type,variants.title,vendor');
            if (this.dataset.noResultsCollection && this.dataset.noResultsCollection.trim() !== '') {
              ajaxUrl.searchParams.set('no_results_collection', this.dataset.noResultsCollection);
            }
          } else {
            // use the theme template fallback
            ajaxUrl = new URL(linkURL);
            ajaxUrl.searchParams.set('section_id', 'main-search');
          }

          fetch(ajaxUrl, { method: 'get', signal: searchAbortController.signal })
            .then((response) => {
              if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
              }
              return response.text();
            })
            .then((response) => {
              const template = document.createElement('template');
              template.innerHTML = response;
              let resultsContainer = null;
              if (theme.Shopify.features.predictiveSearch) {
                resultsContainer = template.content.querySelector('#predictive-search-results');
              } else {
                resultsContainer = template.content.querySelector('.section-search-template .product-grid');
              }

              // reset previous contents
              searchAbortController = null;
              this.classList.remove('main-search--has-results', 'main-search--results-on-multiple-lines', 'main-search--no-results');
              resultsBox.innerHTML = '';

              if (theme.Shopify.features.predictiveSearch && resultsContainer) {
                const predictiveResults = resultsContainer.cloneNode(true);
                
                const tabs = predictiveResults.querySelectorAll('.predictive-search-results__tab');
                const tabContents = predictiveResults.querySelectorAll('.predictive-search-results__tab-content');
                
                tabs.forEach(tab => {
                  tab.addEventListener('click', () => {
                    const targetTab = tab.dataset.tab;
                    tabs.forEach(t => t.classList.remove('predictive-search-results__tab--active'));
                    tabContents.forEach(content => {
                      content.classList.remove('predictive-search-results__tab-content--active');
                      if (content.dataset.tabContent === targetTab) {
                        content.classList.add('predictive-search-results__tab-content--active');
                      }
                    });
                    tab.classList.add('predictive-search-results__tab--active');
                  });
                });

                this.classList.remove('main-search--loading');
                const hasResults = predictiveResults.querySelector('.product-grid');
                if (hasResults) {
                  this.classList.add('main-search--has-results');
                  resultsBox.appendChild(predictiveResults);
                  
                  setTimeout(() => {
                    this.updateViewAllButtonsCount(valueToSearch);
                  }, 100);
                  
                  this.moveSearchSuggestions();
                  setTimeout(() => {
                    this.moveSearchSuggestions();
                  }, 200);
                  setTimeout(() => {
                    this.moveSearchSuggestions();
                  }, 500);
                } else {
                  this.classList.add('main-search--no-results');
                  const emptyMessage = document.createElement('div');
                  emptyMessage.className = 'main-search__empty-message';
                  emptyMessage.innerHTML = theme.strings.generalSearchNoResultsWithoutTerms;
                  resultsBox.appendChild(emptyMessage);
                  
                  if (this.dataset.noResultsCollection && this.dataset.noResultsCollection.trim() !== '') {
                    const collectionHandle = this.dataset.noResultsCollection.trim();
                    const button = document.createElement('a');
                    button.href = `/collections/${collectionHandle}`;
                    button.className = 'btn btn--primary button-row__btn';
                    const collectionTitle = collectionHandle.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
                    button.textContent = `View ${collectionTitle}`;
                    emptyMessage.appendChild(button);
                  }
                }
              } else {
                // Fallback for non-predictive search
                const resultsList = resultsContainer || template.content.querySelector('.section-search-template .product-grid');
                if (!resultsList) {
                  this.classList.remove('main-search--loading');
                  this.classList.add('main-search--no-results');
                  const emptyMessage = document.createElement('div');
                  emptyMessage.className = 'main-search__empty-message';
                  emptyMessage.innerHTML = theme.strings.generalSearchNoResultsWithoutTerms;
                  resultsBox.appendChild(emptyMessage);
                  
                  if (this.dataset.noResultsCollection && this.dataset.noResultsCollection.trim() !== '') {
                    const collectionHandle = this.dataset.noResultsCollection.trim();
                    const button = document.createElement('a');
                    button.href = `/collections/${collectionHandle}`;
                    button.className = 'btn btn--primary button-row__btn';
                    const collectionTitle = collectionHandle.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
                    button.textContent = `View ${collectionTitle}`;
                    emptyMessage.appendChild(button);
                  }
                  return;
                }

                const resultsProducts = document.createElement('div');
                resultsProducts.className = 'main-search__results__products collection-listing';
                resultsProducts.innerHTML = '<div></div>';
                const resultsPages = document.createElement('div');
                resultsPages.className = 'main-search__results__pages';

                resultsProducts.firstElementChild.className = resultsList.className;

                // Convert standard blocks into quick search result format
                resultsList.querySelectorAll('.product-block:not(.collection-block):not(.page-block)').forEach((block, index) => {
                  if (index <= resultLimit) {
                    block.classList.add('main-search-result');
                    block.querySelectorAll('.btn.quickbuy-toggle').forEach((el) => el.remove());
                    block.querySelectorAll('.quickbuy-toggle').forEach((el) => el.classList.remove('quickbuy-toggle'));
                    resultsProducts.firstElementChild.appendChild(block);
                  }
                });

                resultsList.querySelectorAll('.product-block.page-block').forEach((block) => {
                  const item = document.createElement('a');
                  item.className = 'main-search-result main-search-result--page';
                  item.href = block.querySelector('a').href;
                  item.innerHTML = '<div class="main-search-result__text"></div>';
                  item.firstElementChild.innerText = block.querySelector('.page-block__title').innerText;
                  resultsPages.appendChild(item);
                });

                this.classList.remove('main-search--loading');

                const areProducts = !!resultsProducts.querySelector('.main-search-result');
                const arePages = !!resultsPages.querySelector('.main-search-result');
                if (areProducts || arePages) {
                  // Numerous results
                  this.classList.add('main-search--has-results');
                  this.classList.toggle('main-search--results-on-multiple-lines', resultsProducts.querySelectorAll('.product-block').length > 4);
                  if (areProducts) {
                    resultsBox.appendChild(resultsProducts);
                  }
                  if (arePages) {
                    const heading = document.createElement('h6');
                    heading.className = 'main-search-result__heading';
                    heading.innerHTML = theme.strings.generalSearchPages;
                    resultsPages.insertAdjacentElement('afterbegin', heading);
                    resultsBox.appendChild(resultsPages);
                  }

                  const allLink = document.createElement('a');
                  allLink.className = 'main-search__results-all-link btn btn--secondary';
                  allLink.href = linkURL;
                  allLink.innerHTML = theme.strings.generalSearchViewAll;
                  resultsBox.appendChild(allLink);
                } else {
                  // No results - show nothing
                  this.classList.add('main-search--no-results');
                  const emptyMessage = document.createElement('div');
                  emptyMessage.className = 'main-search__empty-message';
                  emptyMessage.innerHTML = theme.strings.generalSearchNoResultsWithoutTerms;
                  resultsBox.appendChild(emptyMessage);
                  
                  if (this.dataset.noResultsCollection && this.dataset.noResultsCollection.trim() !== '') {
                    const collectionHandle = this.dataset.noResultsCollection.trim();
                    const button = document.createElement('a');
                    button.href = `/collections/${collectionHandle}`;
                    button.className = 'btn btn--primary button-row__btn';
                    const collectionTitle = collectionHandle.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
                    button.textContent = `View ${collectionTitle}`;
                    emptyMessage.appendChild(button);
                  }
                }
              }
            })
            .catch((error) => {
              console.warn(error);
            });
        }, searchTimeoutThrottle);
      } else if (!valueToSearch.length) {
        // Abandon current search
        this.oldSearchValue = valueToSearch;
        if (searchAbortController !== null) {
          searchAbortController.abort('Existing request not needed');
          searchAbortController = null;
        }
        clearTimeout(searchTimeoutID);

        // Clear results and show empty state if available
        this.classList.remove('main-search--has-results', 'main-search--results-on-multiple-lines', 'main-search--loading', 'main-search--no-results', 'main-search--empty-state');
        resultsBox.innerHTML = '';
        
        // Load empty state
        this.loadEmptyState();
      }
    };

    searchInput.addEventListener('keyup', handleInputChange.bind(this));
    searchInput.addEventListener('change', handleInputChange.bind(this));
  }

  updateViewAllButtonsCount(searchQuery) {
    if (!searchQuery) return;
    
    const form = this.querySelector('.main-search__form');
    if (!form || !form.action) {
      console.warn('Search form not found');
      return;
    }
    
    const searchUrl = new URL(form.action);
    searchUrl.searchParams.set('q', searchQuery);
    searchUrl.searchParams.set('section_id', 'main-search');
    
    fetch(searchUrl, { method: 'get' })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.text();
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const utilityBar = doc.querySelector('.utility-bar__centre');
        if (utilityBar) {
          const countText = utilityBar.textContent.trim();
          const countMatch = countText.match(/(\d+)/);
          if (countMatch) {
            const totalCount = parseInt(countMatch[1], 10);
            
            const updateButtons = () => {
              const viewAllLinks = this.querySelectorAll('[data-view-all-link]');
              
              if (viewAllLinks.length > 0) {
                viewAllLinks.forEach((link) => {
                  const currentText = link.textContent.trim();
                  const baseText = currentText.replace(/\s*\(\d+\)\s*$/, '').trim();
                  link.textContent = `${baseText} (${totalCount})`;
                });
                return true;
              }
              return false;
            };
            
            if (!updateButtons()) {
              setTimeout(() => {
                updateButtons();
              }, 300);
            }
          }
        }
      })
      .catch((error) => {
        console.warn('Could not fetch search count:', error);
      });
  }
};

window.customElements.define('main-search', MainSearch);
